import { Ring } from '@uiball/loaders'
import CheckIcon from 'assets/icons/step-done-check.svg?component'
import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetCreditApplicationInfoQuery, useGetNotariesQuery } from 'redux/api/pledgeApi'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import tw from 'twin.macro'
import { getWsStepStatus } from 'utils'
import Emitter from 'utils/emitter'

const NotaryStatus = () => {
  // Tools
  const { t } = useTranslation()

  // Selectors
  const { id: creditID } = useAppSelector(getActiveCreditApplication)
  const { DECISION_NOTARY } = useAppSelector(getNotificationsSelector)

  // Queries
  const { data: creditInfo, isLoading: isCreditInfoLoading } = useGetCreditApplicationInfoQuery(creditID as string, {
    skip: !creditID
  })

  const { data: notaryList, isLoading: isNotariesLoading } = useGetNotariesQuery(
    {
      search: creditInfo?.notary_id
    },
    {
      skip: !creditInfo
    }
  )

  // Computed values
  const notary = useMemo(() => {
    if (!notaryList) return undefined
    return notaryList[0]
  }, [notaryList])

  const stepStatus = useMemo(() => getWsStepStatus(DECISION_NOTARY), [DECISION_NOTARY])

  // Handlers
  const onBtnClick = () => {
    Emitter.emit(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE)
  }

  return (
    <div tw='flex flex-col gap-y-1 md:gap-y-4'>
      <InfoBlock
        twStyle={tw`flex flex-col gap-4 mt-4 lg:gap-8`}
        isContentLoading={isCreditInfoLoading || isNotariesLoading}
      >
        <Heading variant='body' twStyle={tw`leading-5`}>
          {t('your-notary')}
        </Heading>
        <div tw='grid grid-cols-2 gap-y-8 gap-x-4'>
          <div tw='col-span-2'>
            <Typography variant='subbody'>{t('notary-fio')}</Typography>
            <Typography variant='subbody' weight='bold'>
              {notary?.fio}
            </Typography>
          </div>
          <div tw=''>
            <Typography variant='subbody'>{t('flow-iin')}</Typography>
            <Typography variant='subbody' weight='bold'>
              {notary?.iin}
            </Typography>
          </div>
          <div tw=''>
            <Typography variant='subbody'>{t('notary-phone')}</Typography>
            <Typography variant='subbody' weight='bold'>
              {notary?.phone}
            </Typography>
          </div>
          <div tw='col-span-2'>
            <Typography variant='subbody'>{t('notary-address')}</Typography>
            <Typography variant='subbody' weight='bold'>
              {notary?.full_address}
            </Typography>
          </div>
        </div>
        <Alert>
          <Typography variant='subbody' text>
            {t('flow-notary-warning')}
          </Typography>
        </Alert>
      </InfoBlock>
      <InfoBlock twStyle={tw`flex items-center gap-x-4`}>
        {stepStatus === 'DONE' ? (
          <Fragment>
            <span tw='w-6 h-6 inline-flex items-center justify-center flex-shrink-0'>
              <CheckIcon tw='w-6 h-6' />
            </span>
            <Typography variant='subbody' weight='bold'>
              {t('flow-notary-step-done')}
            </Typography>
          </Fragment>
        ) : (
          <Fragment>
            <span tw='w-6 h-6 inline-flex items-center justify-center flex-shrink-0'>
              <Ring color='var(--primary-color)' size={24} />
            </span>
            <Typography variant='subbody' weight='bold'>
              {t('flow-notary-waiting-text')}
            </Typography>
          </Fragment>
        )}
      </InfoBlock>
      <Button variant='primary' twStyle={tw`self-end`} disabled={stepStatus !== 'DONE'} onClick={onBtnClick}>
        <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
          {t('flow-continue')}
        </Typography>
      </Button>
    </div>
  )
}

export default NotaryStatus
